import React, { FC, useMemo, useEffect } from "react";
import { StyleProp, ViewStyle } from "react-native";
import Animated, {
  Easing,
  Extrapolation,
  interpolate,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withDelay,
  withTiming,
} from "react-native-reanimated";
import { style as animatedDotStyle } from "./AnimatedDot.styles";

const ANIMATION_DURATION = 1800;
const ANIMATION_DELAY = 700;

interface AnimatedDotProps {
  readonly style?: StyleProp<ViewStyle>;
}

const AnimatedDot: FC<AnimatedDotProps> = ({ style: customStyle }) => {
  const style = useMemo(() => animatedDotStyle(), []);

  const animation = useSharedValue(0);

  useEffect(() => {
    animation.value = withDelay(
      ANIMATION_DELAY,
      withTiming(ANIMATION_DURATION, {
        duration: ANIMATION_DURATION,
        easing: Easing.inOut(Easing.ease),
      }),
    );
  });

  const dotOpacity = useDerivedValue(
    () => interpolate(animation.value, [0, 100, 1500, 1800], [0, 1, 1, 0], Extrapolation.CLAMP),
    [animation],
  );

  const dotScale = useDerivedValue(
    () => interpolate(animation.value, [100, 500, 1500, 1800], [1, 2, 2, 1], Extrapolation.CLAMP),
    [animation],
  );

  const dotAnimatedStyle = useAnimatedStyle(
    () => ({
      opacity: dotOpacity.value,
      transform: [{ scale: dotScale.value }],
    }),
    [dotOpacity.value, dotScale.value],
  );

  return (
    <Animated.View pointerEvents="box-none" style={[style.dot, customStyle, dotAnimatedStyle]} testID="animated-dot" />
  );
};

export { AnimatedDot };
